import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { JiraIssueData, JiraIssueTypeLiteData, JiraPriorityLiteData, JiraUserLiteData } from '../../../api/endpoints/jira';
import { Button, Colors, remInPx } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { Checkbox } from '../../../ui/Checkbox';
import { LoadingGuard, LoadingNext } from '../../../ui/LoadingGuard';
import { FlexScroller, ScrollerValues } from '../../../ui/Scrollers';
import { DataTable } from '../../common/DataTable';
import { useResolve } from '../../Container';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { JiraCardCustomInfo } from '../../packages/atlassian/JiraCard';
import { SelectJiraIssuesFilterView } from './SelectJiraIssuesFilterView';

const CellImage = styled.img`
    width: 1.5rem;
    height: 1.5rem;
    border-radius: .25rem;
`;

const CellContent = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
`;

const StoryPointBadge = styled(Badge)`
    display: inline-block;
    background: ${Colors.gray200};
    color: ${Colors.text};
    border-radius: 999999px;
    padding: .125rem .375rem;
    min-width: 2.5rem;
    text-align: center;
`;

const TextCell = ({ value }: { value: any }) => {
    return <Copy flush>{value || ''}</Copy>;
};

const KeyCell = ({ value }: { value: any }) => {
    return <Copy flush bold>{value || ''}</Copy>;
};

const StoryPointsCell = ({ value }: { value: JiraCardCustomInfo }) => {
    const sp = value?.value;
    if (!sp && sp !== 0) return null;
    return <StoryPointBadge>{sp}</StoryPointBadge>;
};

const IssueTypeCell = ({ value }: { value: JiraIssueTypeLiteData }) => {
    if (!value) return <span></span>;
    return <CellContent>
        <CellImage src={value.iconUrl} />
    </CellContent>;
};

const PriorityCell = ({ value }: { value: JiraPriorityLiteData }) => {
    if (!value?.iconUrl) return <span></span>;
    return <CellContent>
        <CellImage src={value.iconUrl} />
    </CellContent>;
};

const UserCell = ({ value }: { value: JiraUserLiteData }) => {
    if (!value) return <span></span>;
    return <CellContent>
        {value.avatarUrl && <CellImage src={value.avatarUrl} />}
        <span>{value?.displayName}</span>
    </CellContent>;
};

const columnHelper = createColumnHelper<JiraIssueData>();

const columnSchema = [
    columnHelper.display({
        id: 'select',
        size: remInPx(5),
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected()
                }
                onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
            />
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.issueType!, {
        id: 'issueType',
        size: remInPx(2.5),
        header: () => <span>Type</span>,
        cell: info => <IssueTypeCell value={info.getValue()} />,
    }),
    columnHelper.accessor(row => row.key, {
        id: 'key',
        size: remInPx(6),
        header: () => <span>Key</span>,
        cell: info => <KeyCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.summary!, {
        id: 'summary',
        header: () => <span>Summary</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.custom.customfield_10016 ?? {}, {
        id: 'cf[10016]',
        size: remInPx(5),
        header: () => <span>Points</span>,
        cell: info => <StoryPointsCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.assignee!, {
        id: 'assignee',
        size: remInPx(15),
        header: () => <span>Assignee</span>,
        cell: info => <UserCell value={info.getValue()} />,
    }),
    columnHelper.accessor(row => row.priority!, {
        id: 'priority',
        size: 1,
        header: () => <span>Priority</span>,
        cell: info => <PriorityCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.status!, {
        id: 'status',
        size: remInPx(8),
        header: () => <span>Status</span>,
        cell: info => <TextCell value={info.getValue()?.name} />
    }),
];

const useColumnSchema = (issues: JiraIssueData[]) => {

    const hasStoryPoints = useMemo(() => issues.some(x => !!x.custom.customfield_10016), [issues]);

    return useMemo(() => {
        if (!hasStoryPoints) {
            // Remove the story points column
            return columnSchema.filter(x => x.id !== 'customfield_10016');
        }
        return columnSchema;
    }, [hasStoryPoints]);
};

export const SelectJiraIssuesView = ({ jiraProjectId, onSelect }: { jiraProjectId: string, onSelect(items: any[]): void }) => {

    const api = useResolve(ApiClient);
    const [selection, setSelection] = useState([]);
    const [jql, setJql] = useState('' as string);
    const [sort, setSort] = useState('rank:asc' as string);
    const debouncedJql = useDebounce(jql, 500, { leading: true });

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useInfiniteQuery({
        keepPreviousData: true,
        queryKey: [jiraProjectId, 'issues', debouncedJql, sort],
        queryFn: async ({ pageParam }) => {
            const result = await api.exec(v2.jira.issues.list(jiraProjectId, { jql, sort, startAt: pageParam || 0 }));
            return result;
        },
        getNextPageParam: (lastPage) => {
            const ll = lastPage.items.length || 0;
            return ll > 0 ? (lastPage.startAt + ll) : undefined;
        },
    });

    const issues = useMemo(() => data?.pages?.flatMap(x => x.items) ?? [], [data]);
    const issueIds = useMemo(() => new Set(issues.map(x => x.id)), [issues]);

    // We are using a custom row selection state to keep track of the selected items that includes items that are
    // not currently in the search.  E.g. if you search for some items, select them, and search for some more, those
    // previous items remain selected despite not being in the results.

    const rowSelection = useMemo(() => {
        const lookup = new Set(selection.map(x => x.id));
        return Object.fromEntries(issues.map((x, i) => [`${i}`, lookup.has(x.id)]).filter(([_, v]) => v));
    }, [issues, selection]);

    const onRowSelectionChange = (updater: (old: RowSelectionState) => any) => {
        const nextSelection = new Set(Object.keys(updater(rowSelection)));
        const excluded = selection.filter(x => !issueIds.has(x.id));
        const included = issues.filter((_, i) => nextSelection.has(`${i}`));
        setSelection([...excluded, ...included]);
    };

    const columns = useColumnSchema(issues);

    const table = useReactTable({
        columns,
        data: issues,
        state: { rowSelection },
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        onRowSelectionChange,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    setSort(`${sorting[0].id}:${sorting[0].desc ? 'desc' : 'asc'}`);
                }
                else {
                    setSort('rank:asc');
                }
            }
        },
    });

    const onScroll = ({ top }: ScrollerValues) => {
        if (top >= 1 && hasNextPage) {
            fetchNextPage();
        }
    };


    return <Flexer grow flex="column" gap="1">
        <SelectJiraIssuesFilterView jiraProjectId={jiraProjectId} onChange={setJql} />
        <LoadingGuard loading={isFetching && !isFetchingNextPage}>
            {!!data && <>
                <FlexScroller onScroll={onScroll}>
                    <DataTable table={table} />
                    {hasNextPage && <Flexer flex center padding="1">
                        <LoadingNext loading={isFetchingNextPage} />
                    </Flexer>}
                </FlexScroller>
            </>}
        </LoadingGuard>
        <Flexer flex="row" align="center" justify="space-between">
            <Flexer>
                <Copy flush>{selection.length} issues selected.</Copy>
            </Flexer>
            <Flexer>
                <Button variant="primary" onAction={() => onSelect(selection)}>Import</Button>
            </Flexer>
        </Flexer>
    </Flexer>;
};
